import type { FC } from 'react'
import { useCallback, useState } from 'react'
import type { UserHostDetails } from '@/infra/sse/shared/payloads'
import { WizardModal } from '@/common/components'
import type { StartSessionWizardStep } from '../types'
import { useStartSessionForm } from '../hooks/useStartSessionForm'
import { SessionNameStep } from './steps/SessionNameStep'
import { InitialMessageStep } from './steps/InitialMessageStep'
import { PermissionsModeStep } from './steps/PermissionsModeStep'
import { ReviewStep } from './steps/ReviewStep'

interface StartSessionModalProps {
  readonly onClose: () => void
  readonly projectKey: string
  readonly userHostDetails: UserHostDetails
}

export const StartSessionModal: FC<StartSessionModalProps> = ({
  onClose,
  projectKey,
  userHostDetails,
}) => {
  const [step, setStep] = useState<StartSessionWizardStep>('session_name')

  const {
    form,
    handleSessionNameNext,
    handleInitialMessageNext,
    handlePermissionsNext,
    resetForm,
    startSession,
    isSubmitting,
    error,
  } = useStartSessionForm({ setStep })

  const handleClose = useCallback(() => {
    setStep('session_name')
    resetForm()
    onClose()
  }, [onClose, resetForm])

  const handleSubmit = useCallback(async () => {
    const initialMessage = form.initialMessage.trim() || null
    const success = await startSession(
      projectKey,
      form.sessionName.trim(),
      initialMessage,
      form.dangerouslySkipPermissions,
      userHostDetails,
    )
    if (success) {
      handleClose()
    }
  }, [form, projectKey, userHostDetails, startSession, handleClose])

  return (
    <WizardModal open onClose={handleClose} ariaLabel="Start new session">
      {step === 'session_name' && (
        <SessionNameStep
          initialValue={form.sessionName}
          projectKey={projectKey}
          onNext={handleSessionNameNext}
          onCancel={handleClose}
        />
      )}
      {step === 'initial_message' && (
        <InitialMessageStep
          initialValue={form.initialMessage}
          onNext={handleInitialMessageNext}
          onBack={() => setStep('session_name')}
        />
      )}
      {step === 'permissions' && (
        <PermissionsModeStep
          initialValue={form.dangerouslySkipPermissions}
          onNext={handlePermissionsNext}
          onBack={() => setStep('initial_message')}
        />
      )}
      {step === 'review' && (
        <ReviewStep
          form={form}
          projectKey={projectKey}
          isSubmitting={isSubmitting}
          error={error}
          onSubmit={handleSubmit}
          onBack={() => setStep('permissions')}
        />
      )}
    </WizardModal>
  )
}
